import { ThemeEvents } from '@theme/events';

/**
 * Static announcement carousel — only loaded when progress bar is off
 * and announcement messages are configured.
 */
class CartDrawerAnnouncement extends HTMLElement {
  #current = 0;
  #intervalId;
  #slides = [];
  #track = null;
  #prevButton = null;
  #nextButton = null;
  #ready = false;

  connectedCallback() {
    this.refresh();
    if (this.#ready) return;
    this.addEventListener('mouseenter', this.#pause);
    this.addEventListener('mouseleave', this.#resume);
    document.addEventListener('visibilitychange', this.#onVisibility);
    this.#ready = true;
  }

  disconnectedCallback() {
    this.#pause();
    this.#unbind();
    this.removeEventListener('mouseenter', this.#pause);
    this.removeEventListener('mouseleave', this.#resume);
    document.removeEventListener('visibilitychange', this.#onVisibility);
    this.#ready = false;
  }

  refresh() {
    this.#pause();
    this.#unbind();
    this.#track = this.querySelector('.cart-drawer-announcement__track');
    this.#slides = [...this.querySelectorAll('[data-cart-announcement-slide]')];
    this.#prevButton = this.querySelector('.cart-drawer-announcement__nav--prev');
    this.#nextButton = this.querySelector('.cart-drawer-announcement__nav--next');

    if (!this.#track || this.#slides.length <= 1) return;

    this.#prevButton?.addEventListener('click', this.#onPrev);
    this.#nextButton?.addEventListener('click', this.#onNext);
    this.#show(0, false);
    this.#startAutoplay();
  }

  #unbind() {
    this.#prevButton?.removeEventListener('click', this.#onPrev);
    this.#nextButton?.removeEventListener('click', this.#onNext);
  }

  get #speedMs() {
    const seconds = parseInt(this.getAttribute('data-speed') || '5', 10);
    return (Number.isNaN(seconds) ? 5 : seconds) * 1000;
  }

  #onPrev = () => {
    this.#current = (this.#current - 1 + this.#slides.length) % this.#slides.length;
    this.#show(this.#current);
    this.#pause();
    this.#startAutoplay();
  };

  #onNext = () => {
    this.#current = (this.#current + 1) % this.#slides.length;
    this.#show(this.#current);
    this.#pause();
    this.#startAutoplay();
  };

  #show(index, animate = true) {
    if (!this.#track) return;
    this.#current = index;
    this.#track.classList.toggle('cart-drawer-announcement__track--no-transition', !animate);
    this.#track.style.transform = `translate3d(-${index * 100}%, 0, 0)`;
    this.#slides.forEach((slide, i) => {
      slide.setAttribute('aria-hidden', i === index ? 'false' : 'true');
    });
    if (!animate) {
      requestAnimationFrame(() => {
        this.#track?.classList.remove('cart-drawer-announcement__track--no-transition');
      });
    }
  }

  #startAutoplay() {
    if (
      this.#slides.length <= 1 ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      return;
    }
    this.#intervalId = window.setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;
      this.#current = (this.#current + 1) % this.#slides.length;
      this.#show(this.#current);
    }, this.#speedMs);
  }

  #pause = () => {
    if (this.#intervalId !== undefined) {
      clearInterval(this.#intervalId);
      this.#intervalId = undefined;
    }
  };

  #resume = () => {
    if (this.#intervalId !== undefined || document.hidden) return;
    this.#startAutoplay();
  };

  #onVisibility = () => {
    if (document.hidden) this.#pause();
    else this.#resume();
  };
}

if (!customElements.get('cart-drawer-announcement')) {
  customElements.define('cart-drawer-announcement', CartDrawerAnnouncement);
}

document.addEventListener(ThemeEvents.cartUpdate, () => {
  requestAnimationFrame(() => {
    document.querySelectorAll('cart-drawer-announcement').forEach((node) => {
      if (node instanceof CartDrawerAnnouncement) node.refresh();
    });
  });
});
